import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Autocomplete,
  Card,
  CardHeader,
  CardContent,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import {
  DataGrid,
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useTranslation } from 'react-i18next';
import { heIL } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUser } from '../contexts/UserContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { he } from 'date-fns/locale';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import ResponsiveToolbar from '../components/ResponsiveToolbar';

// Yearly fee update dialog component
const YearlyFeeUpdateDialog = ({ open, onClose, client, onUpdate, feeType }) => {
  const [yearlyFee, setYearlyFee] = useState(client?.YearlyFee || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // When the dialog opens, initialize the fee value from the client
  useEffect(() => {
    if (open && client) {
      setYearlyFee(client?.YearlyFee || '');
      setError('');
    }
  }, [open, client]);
  
  const handleUpdate = async () => {
    setLoading(true);
    setError('');
    
    // Validate the yearly fee is a number >= 0
    let numericFee;
    try {
      numericFee = parseFloat(yearlyFee);
      if (isNaN(numericFee)) {
        throw new Error();
      }
    } catch (e) {
      setError('שכר טרחה חייב להיות מספר תקין');
      setLoading(false);
      return;
    }
    
    if (numericFee < 0) {
      setError('שכר טרחה לא יכול להיות מספר שלילי');
      setLoading(false);
      return;
    }
    
    try {
      // Call to Django's UpdateClientYearlyFeeView (PUT /yearly-fee/update-single/)
      // Expected parameters:
      // - business_id: The client's business ID (exactly 9 digits as required by Django)
      // - yearly_fee: The new yearly fee amount (must be a number >= 0)
      const response = await API.put('/yearly-fee/update-single/', {
        business_id: client.ClientBusinessID,
        yearly_fee: numericFee
      });
      
      // Check response - the Django view should return the updated client object
      if (response.data && response.status === 200) {
        // Django returns an updated client object via ClientSerializer
        onUpdate(response.data);
        onClose();
      } else {
        throw new Error('תשובה לא תקינה מהשרת');
      }
    } catch (error) {
      console.error('Error updating yearly fee:', error);
      // Handle specific error codes from Django
      if (error.response) {
        const status = error.response.status;
        if (status === 400) {
          // Handle validation errors
          if (error.response.data && error.response.data.error) {
            setError(error.response.data.error); // Use Django's Hebrew error message
          } else {
            setError('נתונים לא תקינים. אנא בדוק את ערכי השדות ונסה שנית.');
          }
        } else if (status === 404) {
          setError('הלקוח לא נמצא במערכת.');
        } else if (status === 401 || status === 403) {
          setError('אין לך הרשאות לעדכן שכר טרחה שנתי.');
        } else {
          // For other status codes, use the Django error message if available
          if (error.response.data && error.response.data.error) {
            setError(error.response.data.error);
          } else {
            setError('אירעה שגיאה בעדכון שכר הטרחה. אנא נסה שנית.');
          }
        }
      } else {
        // Network or connection error
        setError('לא ניתן להתחבר לשרת. אנא ודא חיבור אינטרנט תקין ונסה שנית.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle>עדכון שכר טרחה {feeType === 'monthly' ? 'חודשי' : 'שנתי'} עבור {client?.ClientName}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <TextField
          label={`סכום שכר טרחה ${feeType === 'monthly' ? 'חודשי' : 'שנתי'}`}
          fullWidth
          type="number"
          value={yearlyFee}
          onChange={(e) => setYearlyFee(e.target.value)}
          margin="normal"
        />
        
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ביטול</Button>
        <Button 
          onClick={handleUpdate} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'עדכון'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Apply to all clients dialog
const ApplyToAllDialog = ({ open, onClose, onConfirm, clients, feeType }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Check if there are any clients whose yearly fee was already applied this year
  const currentYear = new Date().getFullYear();
  
  // Find any clients with yearly fee applied in the current year
  const clientsWithFeeAppliedThisYear = useMemo(() => {
    if (!clients || !Array.isArray(clients)) return [];
    
    return clients.filter(client => {
      if (client && client?.YearlyFeeLastApplied) {
        const lastAppliedDate = new Date(client?.YearlyFeeLastApplied);
        return lastAppliedDate.getFullYear() === currentYear;
      }
      return false;
    });
  }, [clients, currentYear]);
  
  const hasWarning = feeType !== 'monthly' && clientsWithFeeAppliedThisYear.length > 0;
  
  const handleConfirm = async () => {
    if (!password) {
      setError('נדרשת סיסמה לאישור');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Matching Django backend expected parameters exactly as defined in ApplyYearlyFeeToAllView
      const response = await API.post('/yearly-fee/apply-all/', {
        confirm: true,
        password: password,
        fee_type: feeType // Add the fee type parameter
      });
      
      // Process response according to Django API structure
      if (response.data && response.status === 200) {
        // Response expected structure: {status: 'success', updated_clients: number, total_applied: number}
        onConfirm(response.data);
        onClose();
      } else {
        throw new Error('תגובה לא תקינה מהשרת');
      }
    } catch (error) {
      console.error('Error applying yearly fees:', error);
      // Handle specific error codes from the Django API
      if (error.response && error.response.status === 401) {
        setError('סיסמה שגויה');
      } else if (error.response && error.response.data && error.response.data.error) {
        // Use the Hebrew error message directly from Django backend
        setError(error.response.data.error);
      } else {
        setError('אירעה שגיאה בעת החלת שכר הטרחה. אנא נסה שנית.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle>החלת שכר טרחה {feeType === 'monthly' ? 'חודשי' : 'שנתי'} לכל הלקוחות</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          פעולה זו תוסיף את סכום שכר הטרחה {feeType === 'monthly' ? 'החודשי' : 'השנתי'} לחוב השוטף של כל לקוח. פעולה זו אינה ניתנת לביטול.
        </Alert>
        
        {hasWarning && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              שים לב! נמצאו {clientsWithFeeAppliedThisYear.length} לקוחות שכבר הוחל עליהם חיוב השנה ({currentYear})
            </Typography>
            <Typography variant="body2">
              החלת חיוב פעמיים באותה שנה עלולה לגרום לחיוב כפול. יש לוודא שפעולה זו נדרשת.
            </Typography>
            {clientsWithFeeAppliedThisYear.length <= 5 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  לקוחות עם חיוב קיים השנה:
                </Typography>
                <ul style={{ margin: '4px 0', paddingInlineStart: '20px' }}>
                  {clientsWithFeeAppliedThisYear.map(client => (
                    <li key={client.ClientBusinessID}>
                      {client.ClientName} - הוחל בתאריך {new Date(client?.YearlyFeeLastApplied).toLocaleDateString('he-IL')}
                    </li>
                  ))}
                </ul>
              </Box>
            )}
            {clientsWithFeeAppliedThisYear.length > 5 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                ...ועוד {clientsWithFeeAppliedThisYear.length - 5} לקוחות נוספים
              </Typography>
            )}
          </Alert>
        )}
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <TextField
          label="אישור באמצעות סיסמה"
          fullWidth
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ביטול</Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'אישור והחלה'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Theme configuration
const theme = createTheme({
  direction: 'rtl',
  ...heIL,
  typography: {
    fontSize: 16,
  },
});

// Main page component
const YearlyFeeManagementPage = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { userProfile, userLoading } = useUser();
  const queryClient = useQueryClient();
  
  // State for showing only clients without yearly fee or with yearly fee
  const [filterOption, setFilterOption] = useState("all");
  
  // States for dialogs
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [applyAllDialogOpen, setApplyAllDialogOpen] = useState(false);
  
  // State for success messages
  const [successMessage, setSuccessMessage] = useState('');
  
  // Use useRef to store the initial config value
  // This prevents the UI from flashing with default values
  const initialFeeTypeRef = useRef(null);
  
  // Don't render until we have determined the fee type
  // This is the key change - we don't render ANY UI until we know the fee type
  const [ready, setReady] = useState(false);
  const role = userProfile?.role;
  const uiConfig = userProfile?.ui_config || {};
  const clientFieldsSchema = uiConfig.client_fields_schema || {};
  const ClientAllowedRoles = uiConfig.pages_configs?.ClientDetailPage?.allowed_roles || ["manager", "employee"];
 
  // Get the fee type from userProfile context without setting state that would trigger re-renders
  useEffect(() => {
    // Only proceed if userProfile is loaded or loading is complete
    if (userProfile || !userLoading) {
      let configFeeType = 'yearly'; // Default fallback
      
      // Check if user profile exists and has the config
      if (userProfile && 
          userProfile.ui_config && 
          userProfile.ui_config.YearlyFeeManagementPage && 
          userProfile.ui_config.YearlyFeeManagementPage.type) {
        configFeeType = userProfile.ui_config.YearlyFeeManagementPage.type;
      }
      
      console.log('Setting initial fee type from config:', configFeeType);
      initialFeeTypeRef.current = configFeeType;
      setReady(true);
    }
  }, [userProfile, userLoading]);
  
  // Create a memoized feeType value from the ref
  // This ensures a stable value for components to use
  const feeType = useMemo(() => initialFeeTypeRef.current || 'yearly', [ready]);
  
  // Handler for opening the update dialog
  const handleOpenUpdateDialog = (client) => {
    setSelectedClient(client);
    setUpdateDialogOpen(true);
  };
  
  // Handler for updating a client's yearly fee - improved
  const handleClientUpdate = (updatedClient) => {
    console.log('Client update response:', updatedClient);
    
    // Force refresh instead of optimistic updates to ensure accuracy
    queryClient.invalidateQueries(['yearlyFeeClients'], { refetchActive: true });
    queryClient.invalidateQueries(['yearlyFeeHistory'], { refetchActive: true });
    queryClient.invalidateQueries(['clients'], { refetchActive: true });
    queryClient.invalidateQueries(['client', updatedClient.ClientBusinessID], { refetchActive: true });
    queryClient.invalidateQueries(['clientDebt'], { refetchActive: true });
    
    // Force refetch immediately
    refetchYearlyFeeData();
    
    setSuccessMessage(`שכר הטרחה ${feeType === 'monthly' ? 'החודשי' : 'השנתי'} עבור ${updatedClient.ClientName} עודכן בהצלחה`);
    
    // Clear the message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };
  
  const handleApplyToAll = (result) => {
    console.log('Apply to all response:', result);
    
    // Invalidate queries to force refresh from server
    // This is more reliable than optimistic updates for batch operations
    queryClient.invalidateQueries(['yearlyFeeClients'], { refetchActive: true });
    queryClient.invalidateQueries(['yearlyFeeHistory'], { refetchActive: true });
    queryClient.invalidateQueries(['clients'], { refetchActive: true });
    queryClient.invalidateQueries(['clientsDebt'], { refetchActive: true });
    queryClient.invalidateQueries(['transactions'], { refetchActive: true });
    queryClient.invalidateQueries(['dashboard'], { refetchActive: true });
    
    // Force refetch history to show new batch
    refetchHistoryData();
    
    // Reload main data after a short delay to ensure backend consistency
    setTimeout(() => {
      refetchYearlyFeeData();
    }, 500);
    
    setSuccessMessage(`שכר טרחה ${feeType === 'monthly' ? 'חודשי' : 'שנתי'} הוחל בהצלחה עבור ${result.updated_clients} לקוחות (סה"כ: ₪${result.total_applied})`);
    
    // Clear the message after 5 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 5000);
  };
  
  // Fetch yearly fee data
  const [apiError, setApiError] = useState('');
  
  const { 
    data = { clients: [], stats: { clients_with_fee: 0, clients_without_fee: 0 } }, 
    isLoading: loading, 
    isError,
    refetch: refetchYearlyFeeData
  } = useQuery({
    queryKey: ['yearlyFeeClients', feeType],
    queryFn: async () => {
      try {
        // Call to Django's YearlyFeeManagementView
        const response = await API.get('/yearly-fee/');
        console.log('Yearly fee data response:', response.data);
        
        // Expected structure matching Django response: {clients: [...], stats: {clients_with_fee, clients_without_fee}}
        return response.data;
      } catch (error) {
        console.error('Error fetching yearly fee data:', error);
        // Display backend Hebrew error message if available
        if (error.response && error.response.data && error.response.data.error) {
          setApiError(error.response.data.error);
        } else {
          setApiError('אירעה שגיאה בטעינת נתוני שכר הטרחה');
        }
        throw error;
      }
    },
    enabled: ready, // Only fetch data when properly initialized
    staleTime: 1 * 60 * 1000, // Reduced to 1 minute for more frequent refreshes
    cacheTime: 2 * 60 * 1000, // Reduced to 2 minutes
    refetchOnWindowFocus: true, // Refresh when window regains focus
  });
  
  // Fetch yearly fee history data
  const [historyError, setHistoryError] = useState('');
  
  const {
    data: historyData = [],
    isLoading: historyLoading,
    isError: isHistoryError,
    refetch: refetchHistoryData
  } = useQuery({
    queryKey: ['yearlyFeeHistory', feeType],
    queryFn: async () => {
      try {
        // Call to Django's YearlyFeeHistoryView with fee type parameter
        const response = await API.get('/yearly-fee/history/', {
          params: { fee_type: feeType }
        });
        console.log('History data response:', response.data);
        
        // Expected array structure from Django: Array of {id, timestamp, client_count, note}
        return response.data;
      } catch (error) {
        console.error('Error fetching yearly fee history:', error);
        // Display backend Hebrew error message if available
        if (error.response && error.response.data && error.response.data.error) {
          setHistoryError(error.response.data.error);
        } else {
          setHistoryError('אירעה שגיאה בטעינת היסטוריית שכר הטרחה');
        }
        throw error;
      }
    },
    enabled: ready, // Only run the query when properly initialized
    staleTime: 1 * 60 * 1000, // Reduced to 1 minute for more frequent refreshes
    cacheTime: 2 * 60 * 1000, // Reduced to 2 minutes
    refetchOnWindowFocus: true, // Refresh when window regains focus
  });
  
  // Refresh data function to manually trigger refetch when needed
  const refreshAllData = () => {
    console.log('Manually refreshing all data');
    // Force refetch with skipCache to ensure fresh data
    refetchYearlyFeeData({ cancelRefetch: true });
    refetchHistoryData({ cancelRefetch: true });
    
    // Force invalidate with immediate refetch
    queryClient.invalidateQueries(['yearlyFeeClients'], { refetchActive: true });
    queryClient.invalidateQueries(['yearlyFeeHistory'], { refetchActive: true });
  };
  
  // Add error handling with retry option
  const handleErrorRetry = () => {
    setApiError('');
    setHistoryError('');
    refreshAllData();
  };
  
  // Filter clients based on search term and filter option
  const filteredClients = useMemo(() => {
    let filtered = data.clients || [];
    
    // Filter by search term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((client) => {
        const combined = `${client.ClientName} (${client.ClientBusinessID})`.toLowerCase();
        return combined.includes(lowerSearch);
      });
    }
    
    // Filter clients based on selected filter option
    if (filterOption === "without_fee") {
      filtered = filtered.filter((client) => {
        return !client || 
               client.YearlyFee === undefined || 
               client.YearlyFee === null || 
               client.YearlyFee === "" || 
               client.YearlyFee === 0 ;
      });
    } else if (filterOption === "with_fee") {
      filtered = filtered.filter((client) => {
        return client && 
               client.YearlyFee !== undefined && 
               client.YearlyFee !== null && 
               client.YearlyFee !== "" &&
               client.YearlyFee !== 0;
      });
    }
    
    return filtered;
  }, [data.clients, searchTerm, filterOption]);
  
  // Define columns for the data grid
  const columns = [
    {
      field: 'ClientName',
      headerName:  clientFieldsSchema?.ClientName?.label || 'שם הלקוח',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params?.row || {};
        const canNavigateToClient = ClientAllowedRoles.includes(role);
        
        if (canNavigateToClient) {
          return (
            <Button
              variant="text"
              color="primary"
              onClick={() => navigate(`/client/${row.ClientBusinessID}`)}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {row.ClientName}
            </Button>
          );
        } else {
          return (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.primary',
                textAlign: 'center',
                fontSize: '1.1rem'
              }}
            >
              {row.ClientName}
            </Typography>
          );
        }
      },
    },
    {
      field: 'ClientBusinessID',
      headerName: 'תז/חפ',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'YearlyFee',
      headerName: `שכר טרחה ${feeType === 'monthly' ? 'חודשי' : 'שנתי'}`,
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => row?.YearlyFee || '',
      renderCell: (params) => {
        const yearlyFee = params.row?.YearlyFee;
        if (yearlyFee === undefined || yearlyFee === null || yearlyFee === "") {
          return 'לא הוגדר';
        }
        
        try {
          // Make sure we're formatting a number
          const numericFee = parseFloat(yearlyFee);
          if (isNaN(numericFee)) {
            return 'לא הוגדר';
          }
          return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(numericFee);
        } catch (error) {
          console.error('Error formatting yearly fee:', error);
          return 'תקלה בפורמט';
        }
      },
    },
    {
      field: 'LastUpdated',
      headerName: 'חיוב אחרון',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => row?.YearlyFeeLastApplied || '',
      renderCell: (params) => {
        const lastUpdated = params.row?.YearlyFeeLastApplied;
        if (!lastUpdated) return 'מעולם לא חויב';
        
        try {
          const date = new Date(lastUpdated);
          // Check if it's a valid date
          if (isNaN(date.getTime())) {
            return 'תאריך לא תקין';
          }
          
          return date.toLocaleDateString('he-IL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        } catch (error) {
          console.error('Error formatting date:', error);
          return 'תקלה בפורמט תאריך';
        }
      },
    },
    {
      field: 'actions',
      headerName: 'פעולות',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <Box>
            <IconButton 
              color="primary" 
              onClick={() => handleOpenUpdateDialog(params.row)}
              size="small"
            >
              <EditIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  // Define columns for the history data grid
  const historyColumns = [
    {
      field: 'timestamp',
      headerName: 'תאריך',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString('he-IL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      field: 'note',
      headerName: 'הערות',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'client_count',
      headerName: 'מספר לקוחות',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    }
  ];

  // Return early if not ready - this prevents any "yearly" labels from showing before we know the real value
  if (!ready) {
    return (
      <ThemeProvider theme={theme}>
        <div dir="rtl">
          <Container maxWidth={false} sx={{ width: '100%', px: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                טוען הגדרות...
              </Typography>
            </Box>
          </Container>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div dir="rtl">
        <Container maxWidth={false} sx={{ width: '100%', px: 2 }}>
          <Typography variant="h3" sx={{ mb: 3 }}>
            ניהול שכר טרחה {feeType === 'monthly' ? 'חודשי' : 'שנתי'}
          </Typography>
          
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  סך הכל לקוחות
                </Typography>
                <Typography variant="h3" color="primary">
                  {(data.stats?.clients_with_fee || 0) + (data.stats?.clients_without_fee || 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, textAlign: 'center', height: '100%', bgcolor: '#e8f5e9' }}>
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  לקוחות עם שכר טרחה מוגדר
                </Typography>
                <Typography variant="h3" color="#43a047">
                  {data.stats?.clients_with_fee || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, textAlign: 'center', height: '100%', bgcolor: '#ffebee' }}>
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  לקוחות ללא שכר טרחה מוגדר
                </Typography>
                <Typography variant="h3" color="#e53935">
                  {data.stats?.clients_without_fee || 0}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Success message */}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successMessage}
            </Alert>
          )}
          
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
              title={
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  ניהול שכר טרחה {feeType === 'monthly' ? 'חודשי' : 'שנתי'}
                </Typography>
              }
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            />
            <CardContent>
              {/* Search and Filter row */}
              <ResponsiveToolbar>
                <Autocomplete
                  freeSolo
                  options={data.clients}
                  getOptionLabel={(option) =>
                    `${option.ClientName} (${option.ClientBusinessID})`
                  }
                  inputValue={searchTerm}
                  onInputChange={(event, newInputValue) => {
                    setSearchTerm(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      setSearchTerm(newValue);
                    } else if (newValue && typeof newValue === 'object') {
                      setSearchTerm(
                        `${newValue.ClientName} (${newValue.ClientBusinessID})`
                      );
                    } else {
                      setSearchTerm('');
                    }
                  }}
                  forcePopupIcon={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="חיפוש לפי שם או מזהה"
                      placeholder="הקלד שם או חפ/תז"
                      size="small"
                      sx={{ width: { xs: '100%', md: 250 } }}
                    />
                  )}
                />
                
                <TextField
                  select
                  label="סינון"
                  size="small"
                  value={filterOption}
                  onChange={(e) => setFilterOption(e.target.value)}
                  sx={{ width: { xs: '100%', md: 200 } }}
                >
                  <MenuItem value="all">כל הלקוחות</MenuItem>
                  <MenuItem value="without_fee">לקוחות ללא שכר טרחה</MenuItem>
                  <MenuItem value="with_fee">לקוחות עם שכר טרחה</MenuItem>
                </TextField>
                <Button 
                    variant="contained" 
                    color="secondary" 
                    onClick={() => setApplyAllDialogOpen(true)}
                    sx={{
                      mr: 2,
                      whiteSpace: 'nowrap'
                    }}
                    disabled={!data.stats?.clients_with_fee}
                  >
                   עדכון שכר טרחה {feeType === 'monthly' ? 'חודשי' : 'שנתי'}
                </Button>

              </ResponsiveToolbar>

          {/* Error state handling - show specific error from backend when available */}
              {isError && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={handleErrorRetry}>
                      נסה שנית
                    </Button>
                  }
                >
                  {apiError || 'אירעה שגיאה בטעינת הנתונים. אנא רענן את הדף ונסה שנית.'}
                </Alert>
              )}

              {/* DataGrid */}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                    maxHeight: 'calc(100vh - 500px)',
                    overflowY: 'auto',
                  }}
                >
                  <DataGrid
                    rows={filteredClients}
                    columns={columns}
                    density="comfortable"
                    getRowId={(row) => row.ClientBusinessID}
                    autoHeight
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 25, page: 0 },
                      },
                    }}
                    disableSelectionOnClick
                    localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                    components={{
                      NoRowsOverlay: () => (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          אין תוצאות
                        </Box>
                      ),
                    }}
                    sx={{
                      '.MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                        fontSize: '1.15rem',
                        whiteSpace: 'normal !important',  // ADD THIS - allows header text to wrap
                        lineHeight: '1.2',  
                        overflow: 'visible', 
                      },
                      '.MuiDataGrid-cell': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        fontSize: '1rem',
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Divider and History Section */}
          <Divider sx={{ my: 4 }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            היסטוריית הפעלות
          </Typography>

          {/* History Card */}
          <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
            <CardHeader
              title={
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  היסטוריית הפעלות שכר טרחה {feeType === 'monthly' ? 'חודשי' : 'שנתי'}
                </Typography>
              }
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: 1.5,
              }}
            />
            <CardContent>
              {/* Show history error if present */}
              {isHistoryError && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={handleErrorRetry}>
                      נסה שנית
                    </Button>
                  }
                >
                  {historyError || 'אירעה שגיאה בטעינת היסטוריית שכר הטרחה. אנא רענן את הדף ונסה שנית.'}
                </Alert>
              )}
              
              {historyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <DataGrid
                    rows={historyData}
                    columns={historyColumns}
                    getRowId={(row) => row.id}
                    autoHeight
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10, page: 0 } },
                      sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] },
                    }}
                    disableSelectionOnClick
                    localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                    components={{
                      NoRowsOverlay: () => (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 2 }}>
                          אין היסטוריה להצגה
                        </Box>
                      ),
                    }}
                    sx={{
                      '.MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                        fontSize: '1.15rem',
                        whiteSpace: 'normal !important',  // ADD THIS - allows header text to wrap
                        lineHeight: '1.2',  
                        overflow: 'visible', 
                      },
                      '.MuiDataGrid-cell': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        fontSize: '1rem',
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Update Dialog */}
          {updateDialogOpen && selectedClient && (
            <YearlyFeeUpdateDialog
              open={updateDialogOpen}
              onClose={() => setUpdateDialogOpen(false)}
              client={selectedClient}
              onUpdate={handleClientUpdate}
              feeType={feeType}
            />
          )}
          
          {/* Apply to All Dialog */}
          {applyAllDialogOpen && (
            <ApplyToAllDialog
              open={applyAllDialogOpen}
              onClose={() => setApplyAllDialogOpen(false)}
              onConfirm={handleApplyToAll}
              clients={data.clients || []} // Pass the clients data to the dialog
              feeType={feeType}
            />
          )}
        </Container>
      </div>
    </ThemeProvider>
  );
};

export default YearlyFeeManagementPage;